import { useState } from 'react'

import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu'
import { Link, useLocation, useRouter } from '@tanstack/react-router'
import { ChevronDown, Dot, Ellipsis, LogOut } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { cx } from '@/lib/cva-config'
import { MenuItem, useMenuAffiliator } from './menu-builder'

interface MenuProps {
  isOpen: boolean | undefined
}

export function Menu({ isOpen }: MenuProps) {
  const menuList = useMenuAffiliator()

  return (
    <ScrollArea className="[&>div>div[style]]:!block">
      <nav className="h-full w-full pt-4">
        <ul
          className={cx(
            'flex flex-col items-start space-y-1 px-2',
            'min-h-[calc(100vh-48px-36px-16px-32px)] lg:min-h-[calc(100vh-32px-40px-32px)]'
          )}
        >
          {menuList.map((parent, idx) => {
            const hasSubMenu = parent.subMenu && parent.subMenu.length > 0

            if (!hasSubMenu) {
              return (
                <div className="w-full" key={idx}>
                  <TooltipProvider disableHoverableContent>
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <Button
                          variant={parent.isActive ? 'primary' : 'ghost'}
                          className="mb-1 h-10 w-full justify-start"
                          asChild
                        >
                          <Link to={parent.to}>
                            <span
                              className={cx(isOpen === false ? '' : 'mr-4')}
                            >
                              {parent.icon}
                            </span>
                            <p
                              className={cx(
                                'max-w-[200px] truncate',
                                isOpen === false
                                  ? '-translate-x-96 opacity-0'
                                  : 'translate-x-0 opacity-100'
                              )}
                            >
                              {parent.label}
                            </p>
                          </Link>
                        </Button>
                      </TooltipTrigger>
                      {isOpen === false && (
                        <TooltipContent side="right">
                          {parent.label}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )
            }

            return (
              <div className="w-full" key={idx}>
                <CollapseMenuButton {...parent} isOpen={isOpen} />
              </div>
            )
          })}
        </ul>
      </nav>
    </ScrollArea>
  )
}

function CollapseMenuButton({
  icon,
  label,
  subMenu,
  isOpen,
}: MenuItem & { isOpen: boolean | undefined }) {
  const isSubmenuActive = subMenu!.some((x) => x.isActive)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(isSubmenuActive)

  return isOpen ? (
    <Collapsible
      open={isCollapsed}
      onOpenChange={setIsCollapsed}
      className="w-full"
    >
      <CollapsibleTrigger
        className="mb-1 [&[data-state=open]>div>div>svg]:rotate-180"
        asChild
      >
        <Button variant="ghost" className="h-10 w-full justify-start">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <span className="mr-4">{icon}</span>
              <p
                className={cx(
                  'max-w-[150px] truncate',
                  isOpen
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-96 opacity-0'
                )}
              >
                {label}
              </p>
            </div>
            <div
              className={cx(
                'whitespace-nowrap',
                isOpen
                  ? 'translate-x-0 opacity-100'
                  : '-translate-x-96 opacity-0'
              )}
            >
              <ChevronDown
                size={18}
                className="transition-transform duration-200"
              />
            </div>
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        {subMenu!.map(({ to, isActive }, index) => (
          <Button
            key={index}
            variant={isActive ? 'secondary' : 'ghost'}
            className="mb-1 h-10 w-full justify-start"
            asChild
          >
            <Link to={to}>
              <span className="ml-2 mr-4">
                <Dot size={18} />
              </span>
              <p
                className={cx(
                  'max-w-[170px] truncate',
                  isOpen
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-96 opacity-0'
                )}
              >
                {label}
              </p>
            </Link>
          </Button>
        ))}
      </CollapsibleContent>
    </Collapsible>
  ) : (
    <DropdownMenu>
      <TooltipProvider disableHoverableContent>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant={isSubmenuActive ? 'primary' : 'ghost'}
                className="mb-1 h-10 w-full justify-start"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center">
                    <span className={cx(isOpen === false ? '' : 'mr-4')}>
                      {icon}
                    </span>
                    <p
                      className={cx(
                        'max-w-[200px] truncate',
                        isOpen === false ? 'opacity-0' : 'opacity-100'
                      )}
                    >
                      {label}
                    </p>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="start" alignOffset={2}>
            {label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent side="right" sideOffset={25} align="start">
        <DropdownMenuLabel className="max-w-[190px] truncate">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {subMenu!.map(({ to, label, isActive }, index) => (
          <DropdownMenuItem key={index} asChild>
            <Link
              className={`cursor-pointer ${isActive && 'bg-secondary'}`}
              to={to}
            >
              <p className="max-w-[180px] truncate">{label}</p>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuArrow className="fill-border" />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
